/**
 * The read-only half of the person primitive: WHO, without a way to change it.
 *
 * `UserPicker` is the input; these two are what every screen that merely reports
 * an assignee draws - a table cell, a card head, a milestone row. They exist so
 * that resolving an id to a face happens in one place: a column that resolved it
 * itself would also invent its own fallback the first time an id came back
 * unknown, and the marks for "nobody" and "cannot say who" would stop meaning
 * the same thing from one table to the next (see `staff.ts`).
 *
 * Neither draws its own translated text. Everything they show is a name, an
 * initial or one of the two marks, none of which belongs in the catalogue - and
 * a component that translated its own "unassigned" would decide the wording for
 * every screen that drew it, which is the rule `formBits` already follows.
 *
 * The overlap is written with utilities rather than the `.crm-stack` class that
 * looks like it was made for it. That name is taken twice in `index.css`: the
 * avatar-stack rules at the top are overridden further down by the
 * background-work deck, which sets `position: relative` and, in mini mode,
 * `display: none`. Reusing it would hand every avatar row a hidden state that
 * belongs to a different component.
 */
import { cn } from "@wa/ui";
import { Avatar } from "../../ui";
import { NOBODY_MARK, staffLabel, useStaff } from "./staff";

/** A ring in the surface colour, so overlapping discs stay separate faces. */
const RING = "shadow-[0_0_0_2.5px_var(--crm-surface)]";
/** Exactly the 10px the discs are pulled together by; matches the ring width. */
const OVERLAP = "-ml-2.5";

export function UserAvatars({
  ids,
  max = 4,
  size = 28,
}: {
  ids: readonly string[];
  /** How many faces before the tail takes over. */
  max?: number;
  size?: number;
}) {
  const { byId } = useStaff();
  // The same id twice comes from a members list that was edited twice. It would
  // collide on the React key and draw one person as two.
  const list = [...new Set(ids)];

  // A cell with nobody on it still has to hold the row's height open, so the
  // mark is drawn rather than nothing.
  if (!list.length) return <span className="text-muted-foreground">{NOBODY_MARK}</span>;

  // A "+1" tail takes exactly the room the face it hides would have taken, so
  // one over the limit is drawn instead of counted.
  const shown = list.length <= max + 1 ? list : list.slice(0, max);
  const rest = list.length - shown.length;
  const names = list.map((id) => staffLabel(byId, id)).join(", ");

  return (
    <span className="flex items-center" role="img" aria-label={names} title={names}>
      {shown.map((id, i) => (
        <Avatar
          key={id}
          name={staffLabel(byId, id)}
          src={byId.get(id)?.avatarUrl}
          size={size}
          className={cn(RING, i > 0 && OVERLAP)}
        />
      ))}
      {rest > 0 ? (
        // Not an `Avatar` with "+3" as its name: that would run the count
        // through `initials`, which is written for names and would cut a
        // three-digit tail in half.
        <span
          className={cn("crm-av crm-av-soft", RING, OVERLAP)}
          style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.33)) }}
          aria-hidden
        >
          +{rest}
        </span>
      ) : null}
    </span>
  );
}

/**
 * One resolved name as text, for a line that has room for words rather than a
 * disc.
 *
 * An empty id gives the "nobody" mark, not the unknown one: a field that was
 * never filled in and a field pointing at somebody the directory cannot name are
 * two different states, and a reader who sees one glyph for both cannot tell a
 * clean unassigned row from a broken one.
 */
export function UserName({ id }: { id: string | null | undefined }) {
  const { byId } = useStaff();
  return <>{id ? staffLabel(byId, id) : NOBODY_MARK}</>;
}
